import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type ContributorTierSlug = 'regulars' | 'anchors' | 'collective';

export interface ContributorTierConfig {
  slug: ContributorTierSlug;
  name: string;
  description: string;
  requirements: {
    spots: number;
    checkins: number;
    reviews: number;
    helpfulVotes?: number;
    usersHelped?: number;
    highScoreSpots?: number;
  };
  colors: {
    bg: string;
    ring: string;
    innerRing: string;
    icon: string;
    text: string;
    subLabel: string;
  };
}

export const CONTRIBUTOR_TIERS: ContributorTierConfig[] = [
  {
    slug: 'regulars',
    name: 'The Regulars',
    description: "You show up. You add spots. You belong here.",
    requirements: { spots: 3, checkins: 100, reviews: 5 },
    colors: {
      bg: '#f5ecd7',
      ring: '#c4a882',
      innerRing: 'rgba(196, 168, 130, 0.3)',
      icon: '#5b3a29',
      text: '#5b3a29',
      subLabel: '#c4a882',
    },
  },
  {
    slug: 'anchors',
    name: 'The Anchors',
    description: "The community leans on what you've built.",
    requirements: { spots: 8, checkins: 500, reviews: 20, helpfulVotes: 1 },
    colors: {
      bg: '#5b3a29',
      ring: '#f5ecd7',
      innerRing: 'rgba(245, 236, 215, 0.25)',
      icon: '#f5ecd7',
      text: '#f5ecd7',
      subLabel: '#c4a882',
    },
  },
  {
    slug: 'collective',
    name: 'Grounded Collective',
    description: "You helped make this what it is.",
    requirements: { spots: 15, checkins: 1000, reviews: 50, usersHelped: 2, highScoreSpots: 3 },
    colors: {
      bg: '#3b2314',
      ring: '#c4a882',
      innerRing: 'rgba(196, 168, 130, 0.2)',
      icon: '#c4a882',
      text: '#fdf8f0',
      subLabel: '#c4a882',
    },
  },
];

export function getTierConfig(slug: ContributorTierSlug): ContributorTierConfig {
  return CONTRIBUTOR_TIERS.find(t => t.slug === slug)!;
}

export function getHighestTier(earned: ContributorTierSlug[]): ContributorTierSlug | null {
  const order: ContributorTierSlug[] = ['collective', 'anchors', 'regulars'];
  for (const tier of order) {
    if (earned.includes(tier)) return tier;
  }
  return null;
}

export interface ContributorStats {
  spots_suggested: number;
  checkins_on_suggested_spots: number;
  reviews_written: number;
  helpful_votes_received: number;
  users_helped: number;
  high_score_spots: number;
  badges_earned: ContributorTierSlug[];
  badge_earned_dates: Record<string, string>;
  current_tier: ContributorTierSlug | null;
}

export function useContributorTier(userId: string | null) {
  // contributor_tiers is authenticated-only by RLS; skip the query for guests
  // so signed-out profile views don't fire a guaranteed permission error.
  const { user } = useAuth();
  return useQuery({
    queryKey: ['contributor-tier', userId],
    queryFn: async (): Promise<ContributorStats | null> => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('contributor_tiers')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching contributor tier:', error);
        return null;
      }

      if (!data) return null;

      return {
        spots_suggested: data.spots_suggested,
        checkins_on_suggested_spots: data.checkins_on_suggested_spots,
        reviews_written: data.reviews_written,
        helpful_votes_received: data.helpful_votes_received,
        users_helped: data.users_helped,
        high_score_spots: data.high_score_spots,
        badges_earned: (data.badges_earned || []) as ContributorTierSlug[],
        badge_earned_dates: (data.badge_earned_dates || {}) as Record<string, string>,
        current_tier: data.current_tier as ContributorTierSlug | null,
      };
    },
    enabled: !!userId && !!user,
    staleTime: 5 * 60 * 1000,
  });
}

// Calculate progress toward next tier
export function getNextTierProgress(stats: ContributorStats | null) {
  if (!stats) return null;

  const earned = stats.badges_earned;
  let nextTier: ContributorTierConfig | null = null;

  if (!earned.includes('regulars')) {
    nextTier = CONTRIBUTOR_TIERS[0];
  } else if (!earned.includes('anchors')) {
    nextTier = CONTRIBUTOR_TIERS[1];
  } else if (!earned.includes('collective')) {
    nextTier = CONTRIBUTOR_TIERS[2];
  }

  if (!nextTier) return null;

  return {
    tier: nextTier,
    progress: {
      spots: { current: stats.spots_suggested, target: nextTier.requirements.spots },
      checkins: { current: stats.checkins_on_suggested_spots, target: nextTier.requirements.checkins },
      reviews: { current: stats.reviews_written, target: nextTier.requirements.reviews },
    },
  };
}
